import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Modal,
  Dropdown,
  Button,
  Input,
  Table,
  Select,
  Empty,
  Form,
  Skeleton,
  Pagination,
} from "antd";
import {
  FiMoreVertical,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiSend,
  FiBookmark,
  FiClock,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiFileText,
  FiFolder,
  FiTrendingUp,
  FiGrid,
  FiList,
  FiCopy,
  FiX,
} from "react-icons/fi";
import { RiSparklingFill } from "react-icons/ri";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getMyPrompts,
  createPrompt,
  updateMyPrompt,
  deleteMyPrompt,
  submitPromptForReview,
  getPromptCategories,
  getSavedPrompts,
} from "@api/prompts/admin";
import { SuccessNotification, ErrorNotification } from "@components";
import "@assets/css/Prompts.css";

const { Option } = Select;
const { TextArea } = Input;

// Avatar colors based on first letter
const getAvatarColor = (letter) => {
  const colors = {
    A: "#8B5CF6", B: "#3B82F6", C: "#10B981", D: "#F59E0B", E: "#EF4444",
    F: "#EC4899", G: "#6366F1", H: "#14B8A6", I: "#F97316", J: "#84CC16",
    K: "#06B6D4", L: "#8B5CF6", M: "#3B82F6", N: "#10B981", O: "#F59E0B",
    P: "#EF4444", Q: "#EC4899", R: "#6366F1", S: "#14B8A6", T: "#F97316",
    U: "#84CC16", V: "#06B6D4", W: "#8B5CF6", X: "#3B82F6", Y: "#10B981",
    Z: "#F59E0B",
  };
  return colors[letter?.toUpperCase()] || "#8B5CF6";
};

const AdminPromptsList = () => {
  const { wid } = useParams();
  const { user } = useSelector((state) => state.auth);
  const [prompts, setPrompts] = useState([]);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid or table
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });

  // Filters
  const [filterVisibility, setFilterVisibility] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [categories, setCategories] = useState([]);

  // Modals
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  // Form
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState("my-prompts");

  useEffect(() => {
    fetchMyPrompts();
    fetchCategories();
  }, []);

  const fetchMyPrompts = async (page = 1, perPage = 12, filters = {}) => {
    setLoading(true);
    try {
      const res = await getMyPrompts(page, perPage, {
        visibility: filters.visibility || filterVisibility,
        category: filters.category || filterCategory,
        search: filters.search !== undefined ? filters.search : searchText,
      });

      setPrompts(res.data.prompts || []);
      setPagination({
        current: res.data.page || 1,
        pageSize: res.data.perPage || 12,
        total: res.data.total || 0,
      });
    } catch (error) {
      console.error("Fetch prompts error", error);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedPrompts = async () => {
    setLoading(true);
    try {
      const res = await getSavedPrompts(1, 50);
      setSavedPrompts(res.data.prompts || []);
    } catch (error) {
      console.error("Fetch saved prompts error", error);
      setSavedPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getPromptCategories();
      setCategories(res.data || []);
    } catch (error) {
      console.error("Fetch categories error", error);
      setCategories([]);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "saved") {
      fetchSavedPrompts();
    } else {
      fetchMyPrompts();
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    fetchMyPrompts(1, pagination.pageSize, { search: value });
  };

  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      await createPrompt({
        title: values.title,
        description: values.description,
        content: values.content,
        category: values.category,
        visibility: values.visibility || "private",
      });
      SuccessNotification("Prompt created successfully");
      setCreateModalVisible(false);
      form.resetFields();
      fetchMyPrompts();
    } catch (error) {
      ErrorNotification(error.response?.data?.error || "Failed to create prompt");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (values) => {
    if (!selectedPrompt) return;
    setSubmitting(true);
    try {
      await updateMyPrompt(selectedPrompt._id, {
        title: values.title,
        description: values.description,
        content: values.content,
        category: values.category,
        visibility: values.visibility,
      });
      SuccessNotification("Prompt updated successfully");
      setEditModalVisible(false);
      setSelectedPrompt(null);
      editForm.resetFields();
      fetchMyPrompts();
    } catch (error) {
      ErrorNotification(error.response?.data?.error || "Failed to update prompt");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (prompt) => {
    Modal.confirm({
      title: "Delete Prompt",
      content: `Are you sure you want to delete "${prompt.title || prompt.name}"?`,
      okText: "Delete",
      okType: "danger",
      centered: true,
      onOk: async () => {
        try {
          await deleteMyPrompt(prompt._id);
          SuccessNotification("Prompt deleted successfully");
          fetchMyPrompts();
        } catch (error) {
          ErrorNotification(error.response?.data?.error || "Failed to delete prompt");
        }
      },
    });
  };

  const handleSubmitForReview = async (prompt) => {
    Modal.confirm({
      title: "Submit for Public Review",
      content: (
        <div>
          <p>Submit "{prompt.title || prompt.name}" for public review?</p>
          <p style={{ color: "#64748b", fontSize: "13px", marginTop: 8 }}>
            Once approved by moderators, your prompt will be visible to all users.
          </p>
        </div>
      ),
      okText: "Submit for Review",
      centered: true,
      onOk: async () => {
        try {
          await submitPromptForReview(prompt._id);
          SuccessNotification("Prompt submitted for review");
          fetchMyPrompts();
        } catch (error) {
          ErrorNotification(error.response?.data?.error || "Failed to submit for review");
        }
      },
    });
  };

  const openEditModal = (prompt) => {
    setSelectedPrompt(prompt);
    editForm.setFieldsValue({
      title: prompt.title || prompt.name,
      description: prompt.description,
      content: prompt.content || prompt.prompt,
      category: prompt.category?._id || prompt.category,
      visibility: prompt.visibility,
    });
    setEditModalVisible(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_review: { className: "prompt-badge-pending", icon: <FiClock size={12} />, label: "Pending" },
      approved: { className: "prompt-badge-approved", icon: <FiCheckCircle size={12} />, label: "Approved" },
      rejected: { className: "prompt-badge-rejected", icon: <FiX size={12} />, label: "Rejected" },
      draft: { className: "prompt-badge-draft", icon: <FiEyeOff size={12} />, label: "Draft" },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`prompt-badge ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getVisibilityBadge = (visibility) => {
    return visibility === "public" ? (
      <span className="prompt-badge prompt-badge-public">
        <FiEye size={12} />
        Public
      </span>
    ) : (
      <span className="prompt-badge prompt-badge-private">
        <FiEyeOff size={12} />
        Private
      </span>
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    SuccessNotification("Prompt copied to clipboard");
  };

  // Dropdown menu for each prompt
  const getDropdownMenu = (record) => (
    <div className="prompts-dropdown-menu">
      <button
        className="prompts-dropdown-item"
        onClick={() => {
          setSelectedPrompt(record);
          setViewModalVisible(true);
        }}
      >
        <FiEye size={16} />
        View Details
      </button>
      <button
        className="prompts-dropdown-item"
        onClick={() => copyToClipboard(record.content || record.prompt)}
      >
        <FiCopy size={16} />
        Copy Prompt
      </button>
      <button
        className="prompts-dropdown-item"
        onClick={() => openEditModal(record)}
      >
        <FiEdit2 size={16} />
        Edit
      </button>
      {record.visibility === "private" && record.moderation?.status !== "pending_review" && (
        <button
          className="prompts-dropdown-item"
          onClick={() => handleSubmitForReview(record)}
        >
          <FiSend size={16} />
          Submit for Review
        </button>
      )}
      <div className="prompts-dropdown-divider" />
      <button
        className="prompts-dropdown-item danger"
        onClick={() => handleDelete(record)}
      >
        <FiTrash2 size={16} />
        Delete
      </button>
    </div>
  );

  // Stats calculation
  const stats = {
    total: pagination.total,
    private: prompts.filter(p => p.visibility === "private").length,
    public: prompts.filter(p => p.visibility === "public").length,
    totalUses: prompts.reduce((sum, p) => sum + (p.used || p.usageCount || 0), 0),
  };

  // Render prompt card
  const renderPromptCard = (prompt) => {
    const title = prompt.title || prompt.name || "Untitled";
    const firstLetter = title.charAt(0);
    const avatarColor = getAvatarColor(firstLetter);

    return (
      <div key={prompt._id} className="prompt-card">
        <div className="prompt-card-header">
          <div
            className="prompt-card-avatar"
            style={{ background: `linear-gradient(135deg, ${avatarColor} 0%, ${avatarColor}aa 100%)` }}
          >
            {firstLetter.toUpperCase()}
          </div>
          <div className="prompt-card-info">
            <h4 className="prompt-card-title">
              <span>{title}</span>
              {prompt.featured?.isFeatured && (
                <span className="featured-badge">
                  <RiSparklingFill size={14} />
                </span>
              )}
            </h4>
            <p className="prompt-card-category">
              <FiFolder size={12} />
              {prompt.category?.name || "Uncategorized"}
            </p>
          </div>
          <Dropdown
            trigger={["click"]}
            overlay={getDropdownMenu(prompt)}
            placement="bottomRight"
          >
            <button className="prompt-card-menu">
              <FiMoreVertical size={18} />
            </button>
          </Dropdown>
        </div>

        <p className="prompt-card-description">
          {prompt.description || prompt.content || prompt.prompt || "No description"}
        </p>

        <div className="prompt-card-footer">
          <div className="prompt-card-badges">
            {getStatusBadge(prompt.moderation?.status || "draft")}
            {getVisibilityBadge(prompt.visibility)}
          </div>
          <div className="prompt-card-stats">
            <span className="prompt-stat">
              <FiTrendingUp />
              {prompt.used || prompt.usageCount || 0}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      title: "Prompt",
      key: "prompt",
      render: (_, record) => {
        const title = record.title || record.name || "Untitled";
        const firstLetter = title.charAt(0);
        const avatarColor = getAvatarColor(firstLetter);

        return (
          <div className="prompts-table-row-prompt">
            <div
              className="prompts-table-avatar"
              style={{ background: `linear-gradient(135deg, ${avatarColor} 0%, ${avatarColor}aa 100%)` }}
            >
              {firstLetter.toUpperCase()}
            </div>
            <div className="prompts-table-info">
              <h4>
                {title}
                {record.featured?.isFeatured && (
                  <span className="featured-badge">
                    <RiSparklingFill size={14} />
                  </span>
                )}
              </h4>
              <p>{record.category?.name || "Uncategorized"}</p>
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 140,
      render: (_, record) => getStatusBadge(record.moderation?.status || "draft"),
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
      key: "visibility",
      width: 120,
      render: (visibility) => getVisibilityBadge(visibility),
    },
    {
      title: "Uses",
      key: "uses",
      width: 80,
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: "#1e1b4b" }}>
          {record.used || record.usageCount || 0}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => (
        <span style={{ color: "#64748b", fontSize: 13 }}>{formatDate(date)}</span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 60,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          overlay={getDropdownMenu(record)}
          placement="bottomRight"
        >
          <button className="prompt-card-menu">
            <FiMoreVertical size={18} />
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="prompts-page">
      {/* Header */}
      <div className="prompts-header">
        <div className="prompts-header-content">
          <div className="prompts-icon-wrapper">
            <FiFileText />
          </div>
          <div className="prompts-title-group">
            <h1>My Prompts</h1>
            <p>Create and manage your custom AI prompts</p>
          </div>
        </div>
        <div className="prompts-header-actions">
          <button
            className="prompts-btn prompts-btn-secondary"
            onClick={() => fetchMyPrompts()}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
          <button
            className="prompts-btn prompts-btn-primary"
            onClick={() => setCreateModalVisible(true)}
          >
            <FiPlus size={18} />
            Create Prompt
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="prompts-tabs">
        <button
          className={`prompts-tab ${activeTab === "my-prompts" ? "active" : ""}`}
          onClick={() => handleTabChange("my-prompts")}
        >
          <FiFileText size={16} />
          My Prompts
          <span className="prompts-tab-badge">{pagination.total}</span>
        </button>
        <button
          className={`prompts-tab ${activeTab === "saved" ? "active" : ""}`}
          onClick={() => handleTabChange("saved")}
        >
          <FiBookmark size={16} />
          Saved
        </button>
      </div>

      {/* Search & Filters */}
      <div className="prompts-toolbar">
        <div className="prompts-search-wrapper">
          <FiSearch size={18} className="prompts-search-icon" />
          <input
            className="prompts-search-input"
            placeholder="Search prompts by name or content..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch(searchText)}
          />
          {searchText && (
            <button
              className="prompts-search-clear"
              onClick={() => {
                setSearchText("");
                handleSearch("");
              }}
            >
              <FiX size={14} />
            </button>
          )}
        </div>

        <div className="prompts-filters">
          <Select
            className="prompts-filter-select"
            placeholder="Visibility"
            allowClear
            value={filterVisibility}
            onChange={(value) => {
              setFilterVisibility(value);
              fetchMyPrompts(1, pagination.pageSize, { visibility: value });
            }}
          >
            <Option value="private">Private</Option>
            <Option value="public">Public</Option>
          </Select>

          <div style={{ display: "flex", gap: 4, background: "#fff", borderRadius: 10, padding: 4, border: "1px solid #e2e8f0" }}>
            <button
              className="prompts-btn prompts-btn-secondary"
              style={{
                height: 36,
                padding: "0 12px",
                background: viewMode === "grid" ? "#f5f3ff" : "transparent",
                border: "none",
                color: viewMode === "grid" ? "#8B5CF6" : "#64748b",
              }}
              onClick={() => setViewMode("grid")}
            >
              <FiGrid size={18} />
            </button>
            <button
              className="prompts-btn prompts-btn-secondary"
              style={{
                height: 36,
                padding: "0 12px",
                background: viewMode === "table" ? "#f5f3ff" : "transparent",
                border: "none",
                color: viewMode === "table" ? "#8B5CF6" : "#64748b",
              }}
              onClick={() => setViewMode("table")}
            >
              <FiList size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="prompts-stats">
        <div className="prompts-stat-card">
          <div className="prompts-stat-icon purple">
            <FiFileText />
          </div>
          <div className="prompts-stat-content">
            <h4>{stats.total}</h4>
            <p>Total Prompts</p>
          </div>
        </div>
        <div className="prompts-stat-card">
          <div className="prompts-stat-icon blue">
            <FiEyeOff />
          </div>
          <div className="prompts-stat-content">
            <h4>{stats.private}</h4>
            <p>Private</p>
          </div>
        </div>
        <div className="prompts-stat-card">
          <div className="prompts-stat-icon green">
            <FiEye />
          </div>
          <div className="prompts-stat-content">
            <h4>{stats.public}</h4>
            <p>Public</p>
          </div>
        </div>
        <div className="prompts-stat-card">
          <div className="prompts-stat-icon orange">
            <FiTrendingUp />
          </div>
          <div className="prompts-stat-content">
            <h4>{stats.totalUses}</h4>
            <p>Total Uses</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="prompts-content-card">
        {loading ? (
          <div className="prompts-skeleton">
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        ) : prompts.length === 0 ? (
          <div className="prompts-empty">
            <div className="prompts-empty-icon">
              <FiFileText />
            </div>
            <h3>No prompts yet</h3>
            <p>Create your first custom AI prompt to get started. Prompts help you save time by reusing your best instructions.</p>
            <button
              className="prompts-btn prompts-btn-primary"
              onClick={() => setCreateModalVisible(true)}
            >
              <FiPlus size={18} />
              Create Your First Prompt
            </button>
          </div>
        ) : viewMode === "grid" ? (
          <>
            <div className="prompts-grid">
              {prompts.map(renderPromptCard)}
            </div>
            <div className="prompts-pagination">
              <span className="prompts-pagination-info">
                Showing {((pagination.current - 1) * pagination.pageSize) + 1} to {Math.min(pagination.current * pagination.pageSize, pagination.total)} of {pagination.total} prompts
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={[12, 24, 48]}
                onChange={(page, pageSize) => {
                  setPagination({ ...pagination, current: page, pageSize });
                  fetchMyPrompts(page, pageSize);
                }}
              />
            </div>
          </>
        ) : (
          <div className="prompts-table-wrapper">
            <Table
              className="prompts-table"
              columns={columns}
              dataSource={prompts}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} prompts`,
                onChange: (page, pageSize) => {
                  setPagination({ ...pagination, current: page, pageSize });
                  fetchMyPrompts(page, pageSize);
                },
              }}
              rowKey={(record) => record._id}
            />
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        className="prompts-modal"
        title="Create New Prompt"
        visible={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={640}
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input placeholder="e.g., Professional Email Writer" size="large" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={2}
              placeholder="Brief description of what this prompt does"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="Prompt Content"
            rules={[{ required: true, message: "Please enter prompt content" }]}
          >
            <TextArea
              rows={6}
              placeholder="Enter your prompt here. Use {{variable}} for dynamic placeholders."
              size="large"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Category">
                <Select placeholder="Select category" allowClear size="large">
                  {categories.map((cat) => (
                    <Option key={cat._id} value={cat._id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="visibility" label="Visibility" initialValue="private">
                <Select size="large">
                  <Option value="private">Private (Only you)</Option>
                  <Option value="public">Public (After approval)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
            <Button size="large" onClick={() => setCreateModalVisible(false)}>
              Cancel
            </Button>
            <Button type="primary" size="large" htmlType="submit" loading={submitting}>
              Create Prompt
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        className="prompts-modal"
        title="Edit Prompt"
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedPrompt(null);
          editForm.resetFields();
        }}
        footer={null}
        width={640}
        centered
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input placeholder="Enter prompt title" size="large" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Brief description" size="large" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Prompt Content"
            rules={[{ required: true, message: "Please enter prompt content" }]}
          >
            <TextArea rows={6} placeholder="Enter your prompt here" size="large" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Category">
                <Select placeholder="Select category" allowClear size="large">
                  {categories.map((cat) => (
                    <Option key={cat._id} value={cat._id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="visibility" label="Visibility">
                <Select size="large">
                  <Option value="private">Private</Option>
                  <Option value="public">Public</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
            <Button size="large" onClick={() => setEditModalVisible(false)}>
              Cancel
            </Button>
            <Button type="primary" size="large" htmlType="submit" loading={submitting}>
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        className="prompts-modal"
        title={null}
        visible={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedPrompt(null);
        }}
        footer={
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button size="large" onClick={() => setViewModalVisible(false)}>
              Close
            </Button>
            <Button
              size="large"
              onClick={() => copyToClipboard(selectedPrompt?.content || selectedPrompt?.prompt)}
            >
              <FiCopy size={16} style={{ marginRight: 8 }} />
              Copy Prompt
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setViewModalVisible(false);
                openEditModal(selectedPrompt);
              }}
            >
              <FiEdit2 size={16} style={{ marginRight: 8 }} />
              Edit
            </Button>
          </div>
        }
        width={680}
        centered
      >
        {selectedPrompt && (
          <div className="prompt-view-content">
            <div className="prompt-view-header">
              <div
                className="prompt-view-avatar"
                style={{
                  background: `linear-gradient(135deg, ${getAvatarColor((selectedPrompt.title || selectedPrompt.name || "P").charAt(0))} 0%, ${getAvatarColor((selectedPrompt.title || selectedPrompt.name || "P").charAt(0))}aa 100%)`,
                }}
              >
                {(selectedPrompt.title || selectedPrompt.name || "P").charAt(0).toUpperCase()}
              </div>
              <div className="prompt-view-header-info">
                <h2>{selectedPrompt.title || selectedPrompt.name}</h2>
                <div className="prompt-view-badges">
                  {getStatusBadge(selectedPrompt.moderation?.status || "draft")}
                  {getVisibilityBadge(selectedPrompt.visibility)}
                  {selectedPrompt.category && (
                    <span className="prompt-badge prompt-badge-draft">
                      <FiFolder size={12} />
                      {selectedPrompt.category.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {selectedPrompt.description && (
              <div className="prompt-view-section">
                <p className="prompt-view-section-title">Description</p>
                <p className="prompt-view-section-content">{selectedPrompt.description}</p>
              </div>
            )}

            <div>
              <p className="prompt-view-section-title" style={{ marginBottom: 8 }}>Prompt Content</p>
              <div className="prompt-view-code">
                {selectedPrompt.content || selectedPrompt.prompt || "No content"}
              </div>
            </div>

            <div className="prompt-view-stats">
              <div className="prompt-view-stat">
                <div className="prompt-view-stat-value">{formatDate(selectedPrompt.createdAt)}</div>
                <div className="prompt-view-stat-label">Created</div>
              </div>
              <div className="prompt-view-stat">
                <div className="prompt-view-stat-value">{selectedPrompt.used || selectedPrompt.usageCount || 0}</div>
                <div className="prompt-view-stat-label">Times Used</div>
              </div>
              <div className="prompt-view-stat">
                <div className="prompt-view-stat-value">{selectedPrompt.category?.name || "None"}</div>
                <div className="prompt-view-stat-label">Category</div>
              </div>
            </div>

            {selectedPrompt.moderation?.rejectionReason && (
              <div style={{ background: "#fef2f2", borderRadius: 12, padding: 16 }}>
                <p style={{ fontWeight: 600, color: "#dc2626", margin: "0 0 4px 0" }}>
                  Rejection Reason
                </p>
                <p style={{ color: "#dc2626", margin: 0 }}>
                  {selectedPrompt.moderation.rejectionReason}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPromptsList;
